import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import patientService from "../services/patientService";

/**
 * Resolves the real, numeric patient ID for pages routed as
 * /patients/:patientId/... where :patientId may be:
 *   - a real numeric ID (a provider viewing a specific patient), or
 *   - the literal string "me" (a patient viewing their own data — this is
 *     what Header.jsx's navigation actually sends, since getPatientId()
 *     returns "me" as a placeholder, not a real ID)
 *
 * Backend endpoints under /v1/patients/{patientId}/... expect a real Long,
 * not the string "me" — only PatientController's own /v1/patients/me route
 * has special server-side resolution for that literal value. Every other
 * controller (DailyIntake, Medication, Appointment, etc.) needs the actual
 * numeric ID resolved before calling it, which is what this hook does.
 */
export function useResolvedPatientId() {
  const { patientId: urlPatientId } = useParams();
  const [patientId, setPatientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);

      // Already a real numeric ID in the URL (e.g. a provider viewing a
      // specific patient's record) — use it directly, no resolution needed.
      if (urlPatientId && urlPatientId !== "me" && !isNaN(urlPatientId)) {
        if (!cancelled) {
          setPatientId(urlPatientId);
          setLoading(false);
        }
        return;
      }

      // Otherwise ("me", or missing entirely) — resolve the current
      // logged-in user's own numeric patient ID via their profile.
      const result = await patientService.getCurrentUserPatient();
      if (cancelled) return;

      if (result.success && result.data?.id) {
        setPatientId(result.data.id);
      } else {
        setError("Could not load your patient profile. Please try again.");
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [urlPatientId]);

  return { patientId, loading, error };
}

export default useResolvedPatientId;